import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpService } from '../../../services/http.service';
import { ToastService } from '../../../services/toast.service';
import { environment } from '../../../../environments/environment';

interface EventType {
  value: string;
  label: string;
  description: string;
}

interface DialogData {
  mode: 'create' | 'edit';
  event?: any;
  eventTypes: EventType[];
}

@Component({
  selector: 'app-scheduled-event-dialog',
  templateUrl: './scheduled-event-dialog.component.html',
  styleUrls: ['./scheduled-event-dialog.component.scss']
})
export class ScheduledEventDialogComponent implements OnInit {
  public form: FormGroup;
  public eventTypes: EventType[] = [];
  public isEditing = false;
  public saving = false;
  public filePatterns: string[] = [];

  // Common cron expressions
  public cronPresets = [
    { label: 'Every minute', value: '* * * * *' },
    { label: 'Every 5 minutes', value: '*/5 * * * *' },
    { label: 'Every 15 minutes', value: '*/15 * * * *' },
    { label: 'Every 30 minutes', value: '*/30 * * * *' },
    { label: 'Every hour', value: '0 * * * *' },
    { label: 'Every 2 hours', value: '0 */2 * * *' },
    { label: 'Every 6 hours', value: '0 */6 * * *' },
    { label: 'Every 12 hours', value: '0 */12 * * *' },
    { label: 'Daily at midnight', value: '0 0 * * *' },
    { label: 'Daily at 6 AM', value: '0 6 * * *' },
    { label: 'Daily at 12 PM', value: '0 12 * * *' },
    { label: 'Daily at 6 PM', value: '0 18 * * *' },
    { label: 'Weekly (Sunday midnight)', value: '0 0 * * 0' },
    { label: 'Weekly (Monday 9 AM)', value: '0 9 * * 1' },
    { label: 'Monthly (1st at midnight)', value: '0 0 1 * *' },
    { label: 'Monthly (1st at 9 AM)', value: '0 9 1 * *' }
  ];

  public timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
    'UTC'
  ];

  constructor(
    private fb: FormBuilder,
    private httpService: HttpService,
    private toastService: ToastService,
    public dialogRef: MatDialogRef<ScheduledEventDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.eventTypes = data.eventTypes || [];
    this.isEditing = data.mode === 'edit';

    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]],
      eventType: ['', Validators.required],
      cronExpression: ['', [Validators.required, this.cronValidator]],
      timezone: ['UTC', Validators.required],
      isActive: [true],
      configuration: this.fb.group({})
    });
  }

  ngOnInit(): void {
    // Populate form if editing
    if (this.isEditing && this.data.event) {
        this.populateForm();
    }
}

private populateForm(): void {
    const event = this.data.event;

    // Populate basic fields
    this.form.patchValue({
        name: event.name || '',
        description: event.description || '',
        eventType: event.eventType || '',
        cronExpression: event.cronExpression || '',
        timezone: event.timezone || 'America/Chicago',
        isActive: event.isActive !== undefined ? event.isActive : true
    });

    // Handle configuration
    if (event.configuration) {
        // Remove existing configuration controls
        const configGroup = this.form.get('configuration') as FormGroup;
        Object.keys(configGroup.controls).forEach(key => {
            configGroup.removeControl(key);
        });

        // Re-create configuration based on event type
        this.onEventTypeChange();

        // Populate configuration values
        Object.keys(event.configuration).forEach(key => {
            if (configGroup.get(key)) {
                configGroup.get(key)?.setValue(event.configuration[key]);
            }
        });

        // Special handling for data cleanup file patterns
        if (event.eventType === 'data_cleanup' && event.configuration.filePatterns) {
            this.filePatterns = Array.isArray(event.configuration.filePatterns)
                ? event.configuration.filePatterns
                : event.configuration.filePatterns.split(',');
        }
    }

    console.log('Form populated with:', this.form.value);
}

  cronValidator(control: any) {
    if (!control.value) return null;

    // Basic cron validation - 5 parts separated by spaces
    const parts = control.value.trim().split(/\s+/);
    if (parts.length !== 5) {
      return { invalidCron: true };
    }

    return null;
  }

  getEventTypeDescription(eventType: string): string {
    const type = this.eventTypes.find(t => t.value === eventType);
    return type ? type.description : '';
  }

  onEventTypeChange(): void {
    const eventType = this.form.get('eventType')?.value;
    const configGroup = this.form.get('configuration') as FormGroup;

    // Clear existing configuration controls
    Object.keys(configGroup.controls).forEach(key => {
        configGroup.removeControl(key);
    });

    // Add configuration controls based on event type
    switch (eventType) {
        case 'backup':
            configGroup.addControl('backupName', this.fb.control(''));
            break;

        case 'export':
            configGroup.addControl('exportType', this.fb.control('production'));
            configGroup.addControl('fileFormat', this.fb.control('xlsx'));
            configGroup.addControl('selectedFields', this.fb.control(''));
            configGroup.addControl('filterModel', this.fb.control('{}'));
            break;

        case 'ai_processing':
            configGroup.addControl('processingType', this.fb.control('collections'));
            break;

        case 'data_cleanup':
            configGroup.addControl('daysOld', this.fb.control(30));
            configGroup.addControl('cleanupType', this.fb.control('export_files'));
            configGroup.addControl('uploadsDirectory', this.fb.control('./uploads'));
            configGroup.addControl('preserveRecent', this.fb.control(true));
            configGroup.addControl('dryRun', this.fb.control(true));

            // Initialize file patterns if not already set
            if (!this.filePatterns || this.filePatterns.length === 0) {
                this.filePatterns = ['*.xlsx', '*.csv'];
            }
            break;

        case 'report_generation':
            configGroup.addControl('reportType', this.fb.control('summary'));
            configGroup.addControl('includeCharts', this.fb.control(true));
            configGroup.addControl('recipients', this.fb.control(''));
            break;
    }
  }

  setCronPreset(value: string): void {
    this.form.get('cronExpression')?.setValue(value);
  }

  save(): void {
    if (this.form.invalid) {
        this.form.markAllAsTouched();
        return;
    }

    this.saving = true;
    const formValue = this.form.value;

    // Prepare event data
    const eventData = {
        name: formValue.name,
        description: formValue.description,
        eventType: formValue.eventType,
        cronExpression: formValue.cronExpression,
        timezone: formValue.timezone,
        isActive: formValue.isActive,
        configuration: { ...formValue.configuration }
    };

    // Add file patterns for data cleanup
    if (formValue.eventType === 'data_cleanup') {
        eventData.configuration.filePatterns = this.filePatterns;
    }

    console.log('Saving event data:', eventData);

    const eventId = this.data.event?._id || this.data.event?.id;

    const request = this.isEditing && eventId
        ? this.httpService.patch(environment.API_PATH + `/scheduler/events/${eventId}`, eventData)
        : this.httpService.post(environment.API_PATH + '/scheduler/events', eventData);

    request.subscribe({
        next: (response: any) => {
            console.log('Save response:', response);
            this.toastService.success(`Event ${this.isEditing ? 'updated' : 'created'} successfully`);
            this.dialogRef.close(true);
        },
        error: (error: any) => {
            console.error('Error saving event:', error);
            this.toastService.error(`Error ${this.isEditing ? 'updating' : 'creating'} event: ${error.message || 'Unknown error'}`);
            this.saving = false;
        }
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  testCronExpression(): void {
    const cronExpression = this.form.get('cronExpression')?.value;
    if (!cronExpression) return;

    this.httpService.post(environment.API_PATH + '/scheduler/validate-cron', {
      cronExpression,
      timezone: this.form.get('timezone')?.value
    }).subscribe({
      next: (result: any) => {
        if (result.valid) {
          this.toastService.success(`Valid cron expression! ${result.message}`);
        } else {
          this.toastService.error(`Invalid cron expression: ${result.message}`);
        }
      },
      error: (error: any) => {
        console.error('Error validating cron:', error);
        this.toastService.error('Error validating cron expression');
      }
    });
  }

  // File pattern management methods
  addFilePattern(pattern: string): void {
    if (pattern && pattern.trim() && !this.filePatterns.includes(pattern.trim())) {
      this.filePatterns.push(pattern.trim());
      this.updateFilePatterns();
    }
  }

  removeFilePattern(index: number): void {
    this.filePatterns.splice(index, 1);
    this.updateFilePatterns();
  }

  private updateFilePatterns(): void {
    const configGroup = this.form.get('configuration') as FormGroup;
    if (configGroup && configGroup.get('filePatterns')) {
      configGroup.get('filePatterns')?.setValue([...this.filePatterns]);
    }
  }
}
